import React, { useState, useEffect, useMemo } from 'react';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount } from '../../components/common/AdvancedFilter/periodUtils';
// import OverviewView from './components/OverviewView';
import PaymentForm from './components/PaymentForm';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';

const getEmptyPaymentFilters = () => ({
  dateFrom: '',
  dateTo: '',
  assignedUser: '',
  createdBy: '',
  service: '',
  department: '',
  client: '',
  status: '',
  period: '',
  startDate: '',
  endDate: ''
});

const Payment = () => {
  const navigate = useNavigate();
  const { loadPersistedState, persistState } = usePersistedFilters('payment');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    client: '',
    invoice: '',
    paymentMethod: '',
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    note: '',
    file: null
  });
  const [formErrors, setFormErrors] = useState({});
  const [filterData, setFilterData] = useState(getEmptyPaymentFilters);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyPaymentFilters);
  
  // ✅ TAMBAHKAN STATE UNTUK API DATA
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1, // 1-based for GenericView/TablePagination
    totalPages: 0,
    totalElements: 0,
    totalRows: 0,
    rowsPerPage: 10
  });

  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();
  
  // Helper function to check payment-specific capabilities
  const canPayment = (capabilityKey) => can(capabilityKey, 'payment');

  // Default currency fallback (used when payment DTO doesn't carry currencyCode)
  const paymentCurrencyCode = 'IDR';

  // State for filter options
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    paymentMethods: [
      { value: 'CASH', label: 'Cash' },
      { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
      { value: 'CREDIT_CARD', label: 'Credit Card' },
      { value: 'DEBIT_CARD', label: 'Debit Card' },
      { value: 'E_WALLET', label: 'E-Wallet' },
      { value: 'CHECK', label: 'Check' },
      { value: 'OTHER', label: 'Other' }
    ],
    assignedUsers: [],
    createdByUsers: [],
    services: [],
    departments: []
  });

  // ✅ FETCH PAYMENTS DATA
  const fetchPayments = async (params = {}, skipFilter = false, filtersOverride = null) => {
    setLoading(true);
    try {
      // Normalize 1-based UI to 0-based API, and PREVENT override by params spread
      const requestedPage = params.page ?? pagination.currentPage; // 1-based
      const requestedSize = params.size ?? pagination.rowsPerPage;
      const queryParams = {
        page: Math.max(0, (requestedPage || 1) - 1),
        size: requestedSize,
        sortBy: 'id',
        sortDir: 'desc'
      };
      const activeFilters = filtersOverride ?? appliedFilters;
      const effectiveStartDate = activeFilters.startDate || activeFilters.dateFrom;
      const effectiveEndDate = activeFilters.endDate || activeFilters.dateTo;
      
      // Add filter params (skip if skipFilter is true)
      if (!skipFilter) {
        if (effectiveStartDate) queryParams.startDate = effectiveStartDate;
        if (effectiveEndDate) queryParams.endDate = effectiveEndDate;
        if (activeFilters.assignedUser) queryParams.assignedTo = activeFilters.assignedUser;
        if (activeFilters.createdBy) queryParams.createdById = activeFilters.createdBy;
        if (activeFilters.service) queryParams.serviceId = activeFilters.service;
        if (activeFilters.department) queryParams.departmentId = activeFilters.department;
        if (activeFilters.client) queryParams.clientId = activeFilters.client;
        if (activeFilters.status) queryParams.status = activeFilters.status;
      }

      // ✅ SEARCH: Add search query parameter
      if (searchQuery && searchQuery.trim() !== '') {
        queryParams.search = searchQuery.trim();
      }

      // ✅ Permission-based filtering hierarchy:
      // 1. view_global → see all (no filter)
      // 2. view_department → filter by user's department
      // 3. view_own → filter by user ID (created by OR assigned to)
      if (!canPayment('view_global')) {
        if (canPayment('view_department')) {
          // Filter by department
          if (user?.departmentId) {
            queryParams.viewDepartmentId = user.departmentId;
          }
        } else if (canPayment('view_own')) {
          // Filter by user
          if (user?.id) {
            queryParams.viewOwnUserId = user.id;
          }
        }
      }
      
      const response = await api.get('/crm/payments', {
        params: queryParams
      });
      
      const paymentsData = response.data.content || [];

      // currencyCode is already included in each payment DTO — no invoice fetch needed.
      const paymentsWithCurrency = paymentsData;

      // ✅ DATE FILTER: Apply client-side date filtering if backend doesn't filter
      // This is a fallback in case the backend doesn't implement date filtering
      let finalPayments = paymentsWithCurrency;
      let finalTotalElements = response.data.totalElements ?? 0;
      
      if (!skipFilter && (effectiveStartDate || effectiveEndDate)) {
        finalPayments = paymentsWithCurrency.filter(payment => {
          if (!payment.paymentDate) return false;
          
          const paymentDate = new Date(payment.paymentDate);
          paymentDate.setHours(0, 0, 0, 0);
          
          // Check startDate
          if (effectiveStartDate) {
            const fromDate = new Date(effectiveStartDate);
            fromDate.setHours(0, 0, 0, 0);
            if (paymentDate < fromDate) return false;
          }
          
          // Check endDate
          if (effectiveEndDate) {
            const toDate = new Date(effectiveEndDate);
            toDate.setHours(23, 59, 59, 999); // End of day
            if (paymentDate > toDate) return false;
          }
          
          return true;
        });
        
        // ✅ FIX: Recalculate pagination if client-side filtering was applied
        // Note: This is approximate since we only have the current page's data
        // For accurate pagination, date filtering should be implemented in the backend
        finalTotalElements = finalPayments.length;
      }
      
      setPayments(finalPayments);
      // Strictly trust server totals to avoid UI drift (unless client-side filtering was applied)
      const serverTotalPages = response.data.totalPages ?? 0;
      const serverNumber = response.data.currentPage ?? response.data.number ?? 0; // 0-based
      const serverSize = response.data.size ?? requestedSize;
      const calculatedTotalPages = Math.ceil(finalTotalElements / serverSize);
      
      setPagination(prev => ({
        currentPage: serverNumber + 1, // Convert 0-based to 1-based
        totalPages: calculatedTotalPages,
        totalElements: finalTotalElements,
        totalRows: finalTotalElements,
        rowsPerPage: serverSize
      }));
    } catch (error) {
      console.error('Error fetching payments:', error);
      setPayments([]);
      setPagination(prev => ({ currentPage: 1, totalPages: 0, totalElements: 0, totalRows: 0, rowsPerPage: prev.rowsPerPage }));
    } finally {
      setLoading(false);
    }
  };

  // ✅ FETCH STATS
  const fetchStats = async () => {
    try {
      const response = await api.get('/crm/payments/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching payment stats:', error);
      setStats({});
    }
  };

  // ✅ SEARCH: Handle search on Enter key press
  const handleSearch = () => {
    // Reset to first page when searching
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    persistState({ filters: appliedFilters, search: searchQuery, page: 0, size: pagination.rowsPerPage });
    fetchPayments({ page: 1 });
  };

  // ✅ SEARCH: Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // ✅ SEARCH: Handle search input change (only update state, no real-time search)
  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  // ✅ FETCH FILTER OPTIONS
  const fetchFilterOptions = async () => {
    try {
      // Fetch clients - fetch all companies from API
      let clients = [];
      try {
        const clientsRes = await api.get('/crm/companies/options');
        const clientsData = Array.isArray(clientsRes.data) ? clientsRes.data : [];
        clients = clientsData.map(company => ({
          value: company.id || company.value,
          label: company.companyName || company.name || company.label
        }));
      } catch (err) {
        console.warn('Error fetching clients for payment filter (non-critical):', err);
        clients = [];
      }
      
      // Fetch assigned users - filter based on permissions
      let assignedUsers = [];
      try {
        const usersRes = await api.get('/md/internal-users/option-internal-user');
        let usersData = usersRes.data || [];
        
        // ✅ Permission-based filtering for assigned users:
        // 1. view_own → only show current user
        // 2. view_department → only show users in same department
        // 3. view_global → show all users
        if (!canPayment('view_global')) {
          if (canPayment('view_own')) {
            // Only show current user
            if (user?.id) {
              const userId = String(user.id);
              usersData = usersData.filter(u => String(u.id) === userId);
            }
          } else if (canPayment('view_department')) {
            // Only show users in same department
            if (user?.departmentId) {
              const userDeptId = String(user.departmentId);
              usersData = usersData.filter(u => {
                // Handle both departmentId and departementId field names, and type conversion
                const uDeptId = u.departmentId || u.departementId;
                return uDeptId != null && String(uDeptId) === userDeptId;
              });
            } else {
              // If user has no departmentId, show empty array
              usersData = [];
            }
          }
        }
        
        assignedUsers = usersData.map(u => ({
          value: u.id,
          label: u.fullName || `${u.firstName} ${u.lastName}`.trim(),
          departmentId: u.departmentId || u.departementId,
          status: u.status
        }));
      } catch (err) {
        console.error('Error fetching assigned users:', err);
        assignedUsers = [];
      }
      
      // Fetch created by users - filter based on permissions
      let createdByUsers = [];
      try {
        const usersRes = await api.get('/md/internal-users/option-internal-user');
        let usersData = usersRes.data || [];
        
        // ✅ Permission-based filtering for created by users:
        // 1. view_own → only show current user
        // 2. view_department → only show users in same department
        // 3. view_global → show all users
        if (!canPayment('view_global')) {
          if (canPayment('view_own')) {
            // Only show current user
            if (user?.id) {
              const userId = String(user.id);
              usersData = usersData.filter(u => String(u.id) === userId);
            }
          } else if (canPayment('view_department')) {
            // Only show users in same department
            if (user?.departmentId) {
              const userDeptId = String(user.departmentId);
              usersData = usersData.filter(u => {
                // Handle both departmentId and departementId field names, and type conversion
                const uDeptId = u.departmentId || u.departementId;
                return uDeptId != null && String(uDeptId) === userDeptId;
              });
            } else {
              // If user has no departmentId, show empty array
              usersData = [];
            }
          }
        }
        
        createdByUsers = usersData.map(u => ({
          value: u.id,
          label: u.fullName || `${u.firstName} ${u.lastName}`.trim()
        }));
      } catch (err) {
        console.error('Error fetching created by users:', err);
        createdByUsers = [];
      }
      
      // Fetch services - fetch all services from API
      let services = [];
      try {
        const servicesRes = await api.get('/crm/services/options');
        const servicesData = servicesRes.data || [];
        services = servicesData.map(service => ({
          value: service.value || service.id,
          label: service.label || service.name
        }));
      } catch (err) {
        console.error('Error fetching services:', err);
        services = [];
      }
      
      // Fetch departments - filter based on permissions
      let departments = [];
      try {
        const deptRes = await api.get('/md/departement');
        let deptData = deptRes.data || [];
        if (!canPayment('view_global')) {
          if (user?.departmentId) {
            const userDeptId = String(user.departmentId);
            deptData = deptData.filter(dept => String(dept.id || '') === userDeptId);
          } else {
            deptData = [];
          }
        }
        departments = deptData.map(dept => ({
          value: dept.id,
          label: dept.name
        }));
      } catch (err) {
        console.error('Error fetching departments:', err);
        departments = [];
      }
      
      setFilterOptions(prev => ({
        ...prev,
        clients,          // All companies from API
        assignedUsers,    // All users from API (filtered by permissions)
        createdByUsers,   // All users from API (filtered by permissions)
        services,         // All services from API
        departments       // All departments from API (filtered by permissions)
      }));
    } catch (error) {
      console.error('Error fetching filter options:', error);
      console.error('Filter options error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Don't show alert for filter options error as it's not critical
      // Just set empty arrays to prevent crashes
      setFilterOptions(prev => ({
        ...prev,
        clients: [],
        assignedUsers: [],
        createdByUsers: [],
        services: [],
        departments: []
      }));
    }
  };

  // ✅ LOAD DATA ON COMPONENT MOUNT (wait for capabilities to load)
  useEffect(() => {
    if (!capabilitiesLoading) {
      const initializeData = async () => {
        try {
          const saved = loadPersistedState();
          if (saved) {
            const restoredFilters = saved.filters ?? getEmptyPaymentFilters();
            const restoredSearch = saved.search ?? '';
            const restoredPage = saved.page ?? 0;
            const restoredSize = saved.size ?? 10;

            setFilterData(restoredFilters);
            setAppliedFilters(restoredFilters);
            setSearchQuery(restoredSearch);
            setPagination(prev => ({ ...prev, currentPage: restoredPage + 1, rowsPerPage: restoredSize }));

            await Promise.allSettled([
              fetchPayments({ page: restoredPage + 1, size: restoredSize }, false, restoredFilters),
              fetchStats(),
              fetchFilterOptions()
            ]);
          } else {
            await Promise.allSettled([
              fetchPayments(),
              fetchStats(),
              fetchFilterOptions()
            ]);
          }
        } catch (error) {
          console.error('Error during payment initialization:', error);
        }
      };

      initializeData();
    }
  }, [capabilitiesLoading]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  // ✅ HANDLE PAGINATION
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    persistState({ filters: appliedFilters, search: searchQuery, page: newPage - 1, size: pagination.rowsPerPage });
    fetchPayments({ page: newPage });
  };

  const handleRowsPerPageChange = (newSize) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newSize, currentPage: 1 }));
    persistState({ filters: appliedFilters, search: searchQuery, page: 0, size: newSize });
    fetchPayments({ page: 1, size: newSize });
  };

  // Build userId → { deptId, status } lookup — same pattern as Leads/Clients
  const paymentUserDeptLookup = useMemo(() => {
    const map = new Map();
    (filterOptions.assignedUsers || []).forEach(u => {
      if (u.value != null) map.set(String(u.value), { deptId: u.departmentId != null ? String(u.departmentId) : null, status: u.status });
    });
    return map;
  }, [filterOptions.assignedUsers]);

  const withPaymentInactiveBadge = (u) => {
    const entry = paymentUserDeptLookup.get(String(u.value));
    return entry?.status === '0' ? { ...u, label: `${u.label} (Inactive)` } : u;
  };

  // Cascade: filter Assigned To by selected department
  const cascadedPaymentAssignedUsers = useMemo(() => {
    const dept = filterData.department;
    const base = (filterOptions.assignedUsers || []).map(withPaymentInactiveBadge);
    if (!dept) return base;
    return base.filter(u => u.departmentId != null && String(u.departmentId) === String(dept));
  // eslint-disable-next-line
  }, [filterData.department, filterOptions.assignedUsers, paymentUserDeptLookup]);

  const paymentFilterFields = [
    { key: 'department', label: 'Department', type: 'select', options: filterOptions?.departments || [] },
    { key: 'assignedUser', label: 'Assigned To', type: 'select', options: cascadedPaymentAssignedUsers },
    { key: 'status', label: 'Status', type: 'select', options: [
      { value: '', label: 'All' },
      { value: 'Paid', label: 'Paid' },
      { value: 'Partial', label: 'Partial' },
      { value: 'Unpaid', label: 'Unpaid' },
    ]},
    { key: 'createdBy', label: 'Created By', type: 'select', options: filterOptions?.createdByUsers || [] },
    { key: 'service', label: 'Service', type: 'select', options: filterOptions?.services || [] },
    { key: 'client', label: 'Client', type: 'select', options: filterOptions?.clients || [] },
  ];

  const activeFilterCount = getActiveFilterCount(appliedFilters, paymentFilterFields);
  const paymentFilterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({
        key: 'period',
        label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}`,
      });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({
        key: 'period',
        label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}`,
      });
    }

    paymentFilterFields.forEach((field) => {
      const value = appliedFilters[field.key];
      const isEmpty = value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);
      if (isEmpty) {
        return;
      }

      if (Array.isArray(value)) {
        tags.push({ key: field.key, label: `${field.label}: ${value.length} selected` });
        return;
      }

      const option = (field.options || []).find((opt) => String(opt[field.valueField || 'value']) === String(value));
      tags.push({ key: field.key, label: `${field.label}: ${option ? option[field.labelField || 'label'] : value}` });
    });

    return tags;
  }, [appliedFilters, paymentFilterFields]);

  const getClearedPaymentFilters = (filters, key) => {
    const keysToClear = ['period', 'startDate', 'endDate'].includes(key)
      ? ['period', 'startDate', 'endDate']
      : [key];
    const nextFilters = { ...filters };
    keysToClear.forEach((fieldKey) => {
      nextFilters[fieldKey] = '';
    });
    return nextFilters;
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    const emptyFilters = getEmptyPaymentFilters();
    setFilterData(emptyFilters);
    setAppliedFilters(emptyFilters);

    // Reset pagination ke page 1 (UI is 1-based)
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    persistState({ filters: emptyFilters, search: '', page: 0, size: pagination.rowsPerPage });
    fetchPayments({ page: 1 }, false, emptyFilters);
  };

  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    persistState({ filters: applied, search: searchQuery, page: 0, size: pagination.rowsPerPage });
    fetchPayments({ page: 1 }, false, applied);
  };

  const handlePaymentChipRemove = (key) => {
    const keysToCheck = ['period', 'startDate', 'endDate'].includes(key)
      ? ['period', 'startDate', 'endDate']
      : [key];
    const hasActiveValue = keysToCheck.some((fieldKey) => Boolean(appliedFilters[fieldKey]));
    if (!hasActiveValue) return;

    const nextAppliedFilters = getClearedPaymentFilters(appliedFilters, key);
    setFilterData(prev => getClearedPaymentFilters(prev, key));
    setAppliedFilters(nextAppliedFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    persistState({ filters: nextAppliedFilters, search: searchQuery, page: 0, size: pagination.rowsPerPage });
    fetchPayments({ page: 1 }, false, nextAppliedFilters);
  };

  const handleAddNew = () => {
    if (!canPayment('create')) {
      setAlert({
        type: 'danger',
        message: 'You do not have permission to create payments.'
      });
      return;
    }

    setIsEditMode(false);
    setEditingPayment(null);
    setFormData({
      client: '',
      invoice: '',
      paymentMethod: '',
      paymentDate: new Date().toISOString().split('T')[0],
      amount: '',
      note: '',
      file: null
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingPayment(null);
    // Clear form data when closing
    setFormData({
      client: '',
      invoice: '',
      paymentMethod: '',
      paymentDate: new Date().toISOString().split('T')[0],
      amount: '',
      note: '',
      file: null
    });
    setFormErrors({});
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ✅ UPDATE FORM SUBMIT TO USE API
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = {};
    if (!formData.client) errors.client = 'Client is required';
    if (!formData.invoice) errors.invoice = 'Invoice is required';
    if (!formData.paymentMethod) errors.paymentMethod = 'Payment method is required';
    if (!formData.paymentDate) errors.paymentDate = 'Payment date is required';
    if (!formData.amount) errors.amount = 'Amount is required';
    else {
      // Extract numeric value from currency input (handle both string and number)
      let numericAmount;
      if (typeof formData.amount === 'string') {
        numericAmount = parseFloat(formData.amount.replace(/[^\d.-]/g, ''));
      } else {
        numericAmount = parseFloat(formData.amount);
      }
      if (isNaN(numericAmount) || numericAmount <= 0) {
        errors.amount = 'Amount must be greater than 0';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      // Extract numeric amount from currency input (handle both string and number)
      let numericAmount = 0;
      if (formData.amount) {
        if (typeof formData.amount === 'string') {
          numericAmount = parseFloat(formData.amount.replace(/[^\d.-]/g, ''));
        } else {
          numericAmount = parseFloat(formData.amount);
        }
      }
      
      if (isEditMode && editingPayment?.id) {
        // For edit mode, send as JSON
        const editData = {
          clientId: formData.client,
          invoiceId: formData.invoice,
          paymentMethod: formData.paymentMethod,
          paymentDate: formData.paymentDate,
          amount: numericAmount,
          note: formData.note || ''
        };
        
        const response = await api.put(`/crm/payments/${editingPayment.id}`, editData);
        showAlert('success', 'Payment updated successfully!');
      } else {
        // For create mode, handle file upload with FormData
        const data = new FormData();
        data.append('clientId', formData.client);
        data.append('invoiceId', formData.invoice);
        data.append('paymentMethod', formData.paymentMethod);
        data.append('paymentDate', formData.paymentDate);
        data.append('amount', numericAmount);
        data.append('note', formData.note || '');
        if (formData.file && formData.file instanceof File) {
          data.append('document', formData.file);
        }
        
        const response = await api.post('/crm/payments', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        showAlert('success', 'Payment created successfully!');
      }
      
      // Close modal first
      handleModalClose();
      
      // Then refresh data after a short delay to ensure modal cleanup
      setTimeout(() => {
        // For create, go to first page. For edit, stay on current page
        const targetPage = isEditMode ? pagination.currentPage : 1;
        fetchPayments({ page: targetPage });
        fetchStats();
      }, 300);
    } catch (error) {
      console.error('Error saving payment:', error);
      showAlert('danger', `Failed to ${isEditMode ? 'update' : 'create'} payment. Please try again.`);
    }
  };

  const formatCurrency = (amount, currencyCode = paymentCurrencyCode) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Helper: format date as dd/mm/yyyy
  const formatDate = (date) => {
    if (!date) return '-';
    try {
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return date;
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
      return date;
    }
  };

  // Helper: format date with time as dd/mm/yyyy HH:mm
  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return '';
  
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
  
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
  
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
  
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Full Paid': 'bg-success text-white',
      'Half Paid': 'bg-warning text-white',
      'Draft': 'badge-outline text-secondary'
    };
    return `badge ${colors[status] || 'badge-outline'}`;
  };

  // ✅ UPDATE EDIT HANDLER
  const handleEditPayment = (payment) => {
    if (!canPayment('edit')) {
      setAlert({
        type: 'danger',
        message: 'You do not have permission to edit payments.'
      });
      return;
    }

    setIsEditMode(true);
    setEditingPayment(payment);
    setFormData({
      client: payment.clientId,
      invoice: payment.invoiceId,
      paymentMethod: payment.paymentMethod,
      paymentDate: payment.paymentDate,
      amount: payment.amount ? payment.amount.toString() : '',
      note: payment.note,
      file: payment.documentName
    });
    setIsModalOpen(true);
  };

  // ✅ UPDATE DELETE HANDLER
  const handleDeletePayment = (payment) => {
    if (!canPayment('delete')) {
      setAlert({
        type: 'danger',
        message: 'You do not have permission to delete payments.'
      });
      return;
    }

    setItemToDelete(payment);
    setShowDeleteModal(true);
  };

  const confirmDeletePayment = async () => {
    try {
      await api.delete(`/crm/payments/${itemToDelete.id}`);
      fetchPayments({ page: pagination.currentPage });
      fetchStats();
      showAlert('success', 'Payment deleted successfully!');
    } catch (error) {
      console.error('Error deleting payment:', error);
      showAlert('danger', 'Failed to delete payment. Please try again.');
    }
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // ✅ UPDATE COLUMNS FOR API DATA
  const columns = [
    {
      key: 'id',
      label: 'Payment ID',
      render: (value) => `PAY-${String(value).padStart(4, '0')}`
    },
    {
      key: 'clientName',
      label: 'Client Name',
      render: (value) => value || 'Unknown Client'
    },
    {
      key: 'tagging',
      label: 'Tagging',
      render: (value) => {
        if (!value) return <span className="text-muted">-</span>;
        const tags = typeof value === 'string' ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (tags.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {tags.map((tag, i) => (
              <span key={i} className="badge bg-blue-lt text-wrap" title={tag} style={{ fontSize: '0.7rem' }}>
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'invoiceNumber',
      label: 'Invoice Number',
      render: (value) => value || 'Unknown Invoice'
    },
    {
      key: 'paymentMethod',
      label: 'Payment Method',
      render: (value) => value || '-'
    },
    {
      key: 'paymentDate',
      label: 'Payment Date',
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (value, item) => {
        // ✅ MULTI-CURRENCY: Gunakan display currency dari payment atau fallback ke paymentCurrencyCode
        const currencyCode = item?.displayCurrencyCode || item?.currencyCode || paymentCurrencyCode;
        const formattedValue = formatCurrency(value || 0, currencyCode);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '150px', width: '150px' }}
            title={formattedValue}
          >
            {formattedValue}
          </span>
        );
      }
    },
    {
      key: 'note',
      label: 'Note',
      render: (value) => value || '-'
    },
    {
      key: 'createdBy',
      label: 'Created By',
      render: (value) => value || '-'
    },
    {
      key: 'createdAt',
      label: 'Created At',
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'updatedBy',
      label: 'Updated By',
      render: (value) => value || '-'
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      render: (value) => {
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue}
          </span>
        );
      }
    },
    {
      key: 'documentName',
      label: 'File',
      render: (value) => (
        value ? (
          <a href={`/files/${value}`} target="_blank" rel="noopener noreferrer" className="text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
              <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"></path>
            </svg>
          </a>
        ) : '-'
      )
    }
  ];

  // ✅ EXPORT FUNCTIONS
  const handleExportExcel = () => {
    if (!canPayment('export_data')) {
      setAlert({
        type: 'danger',
        message: 'You do not have permission to export data.'
      });
      return;
    }

    try {
      const rows = payments.map(row => ({
        'Payment ID': `PAY-${String(row.id).padStart(4, '0')}`,
        'Client Name': row.clientName || 'Unknown Client',
        'Invoice Number': row.invoiceNumber || 'Unknown Invoice',
        'Payment Method': row.paymentMethod || '-',
        'Payment Date': formatDate(row.paymentDate),
        'Amount': formatCurrency(row.amount),
        'Note': row.note || '-',
        'File': row.documentName || '-'
      }));

      const header = Object.keys(rows[0] || {
        'Payment ID': '',
        'Client Name': '',
        'Invoice Number': '',
        'Payment Method': '',
        'Payment Date': '',
        'Amount': '',
        'Note': '',
        'File': ''
      });

      const csv = [
        header.join(','),
        ...rows.map(r => header.map(h => {
          const cell = r[h] != null ? String(r[h]) : '';
          const needsQuote = /[",\n]/.test(cell);
          const escaped = cell.replace(/"/g, '""');
          return needsQuote ? `"${escaped}"` : escaped;
        }).join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const ts = new Date().toISOString().slice(0,10).replace(/-/g,'');
      link.href = url;
      link.download = `payments_export_${ts}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showAlert('success', 'Payments exported to Excel successfully!');
    } catch (e) {
      console.error('Export CSV failed:', e);
      showAlert('danger', 'Failed to export payments to Excel. Please try again.');
    }
  };

  const handleExportPDF = () => {
    if (!canPayment('export_data')) {
      setAlert({
        type: 'danger',
        message: 'You do not have permission to export data.'
      });
      return;
    }

    try {
      const win = window.open('', '_blank');
      if (!win) return;
      
      const style = `
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; }
          h2 { margin: 0 0 12px 0; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
          th { background: #f5f5f5; }
        </style>
      `;
      
      const header = ['Payment ID','Client Name','Invoice Number','Payment Method','Payment Date','Amount','Note','File'];
      const rows = payments.map(r => {
        // ✅ MULTI-CURRENCY: Get currency code for each payment
        const currencyCode = r?.displayCurrencyCode || r?.currencyCode || paymentCurrencyCode;
        return [
          `PAY-${String(r.id).padStart(4, '0')}`,
          r.clientName || 'Unknown Client',
          r.invoiceNumber || 'Unknown Invoice',
          r.paymentMethod || '-',
          formatDate(r.paymentDate),
          formatCurrency(r.amount || 0, currencyCode),
          r.note || '-',
          r.documentName || '-'
        ];
      });
      
      const table = `
        <table>
          <thead>
            <tr>${header.map(h=>`<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${rows.map(cols => `<tr>${cols.map(c => `<td>${c != null ? String(c) : ''}</td>`).join('')}</tr>`).join('')}
          </tbody>
        </table>
      `;
      
      win.document.write(`<!doctype html><html><head><meta charset="utf-8">${style}<title>Payments Export</title></head><body><h2>Payments Export</h2>${table}</body></html>`);
      win.document.close();
      win.focus();
      win.print();
      
      showAlert('success', 'Payments exported to PDF successfully!');
    } catch (e) {
      console.error('Export PDF failed:', e);
      showAlert('danger', 'Failed to export payments to PDF. Please try again.');
    }
  };

  // ✅ CALCULATE STATS FROM API DATA
  // Note: Stats are calculated in base currency (IDR) from backend
  const calculatedStats = {
    totalPayments: {
      count: stats.totalPayments || 0,
      amount: formatCurrency(stats.totalAmount || 0, paymentCurrencyCode)
    },
    fullPaid: {
      count: payments.filter(payment => payment.status === 'Full Paid').length,
      amount: formatCurrency(payments
        .filter(payment => payment.status === 'Full Paid')
        .reduce((sum, payment) => sum + (payment.amount || 0), 0), paymentCurrencyCode)
    },
    halfPaid: {
      count: payments.filter(payment => payment.status === 'Half Paid').length,
      amount: formatCurrency(payments
        .filter(payment => payment.status === 'Half Paid')
        .reduce((sum, payment) => sum + (payment.amount || 0), 0), paymentCurrencyCode)
    },
    draft: {
      count: payments.filter(payment => payment.status === 'Draft').length,
      amount: formatCurrency(payments
        .filter(payment => payment.status === 'Draft')
        .reduce((sum, payment) => sum + (payment.amount || 0), 0), paymentCurrencyCode)
    }
  };

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
      <div className="row g-2 align-items-center">
        <div className="col">
          {/* <h3 className="page-subtitle m-0">Overview</h3> */}
          <h2 className="page-title">Dashboard Payments</h2>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list d-flex flex-wrap gap-2">
            {canPayment('create') && (
              <button className="btn btn-primary" onClick={handleAddNew}>
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M12 5l0 14"></path>
                  <path d="M5 12l14 0"></path>
                </svg>
                New Payment
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  {/* <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-overview" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">Overview</a>
                  </li> */}
                  <li className="nav-item-tab active" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="false" tabIndex="-1" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  {/* <div className="tab-pane active show" id="tabs-overview" role="tabpanel">
                    <OverviewView stats={calculatedStats} />
                  </div> */}
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={payments}
                      columns={columns}
                      onEdit={canPayment('edit') ? handleEditPayment : undefined}
                      onDelete={canPayment('delete') ? handleDeletePayment : undefined}
                      isLoading={loading || capabilitiesLoading}
                      pagination={{
                        ...pagination,
                        onPageChange: handlePageChange,
                        onRowsPerPageChange: handleRowsPerPageChange
                      }}
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasPayment" activeCount={activeFilterCount} />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                          placeholder="Search payments..."
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={canPayment('export_data') ? handleExportExcel : undefined}
                          onExportPDF={canPayment('export_data') ? handleExportPDF : undefined}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Payments is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={paymentFilterTags.map((tag) => tag.label).join(' · ')}
                          loading={loading}
                          activeCount={activeFilterCount}
                          filterTags={paymentFilterTags}
                          onRemoveFilter={handlePaymentChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      footerContent={
                        <div className="bg-light p-3 mt-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="font-weight-bold">Total Amount:</span>
                            <span className="h4 m-0">{calculatedStats.totalPayments.amount}</span>
                          </div>
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PaymentForm
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        handleFormSubmit={handleFormSubmit}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        isEditMode={isEditMode}
        editingPayment={editingPayment}
        refreshPayments={() => fetchPayments({ page: pagination.currentPage })}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeletePayment}
        title="Delete Payment"
        message={`Are you sure you want to delete this? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
      {/* Filter Offcanvas */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasPayment"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={paymentFilterFields}
      />
    </div>
  );
};

export default Payment; 